/**
 * PHARMAKON — Trésorerie TWP-60 · reading a payload under the cashier's visibility policy (BRIEF_POS §2.1,
 * TO-15 « own count only »).
 *
 * The server is the authority: a figure the officine hides from the CAISSIER is ABSENT from its answer
 * (never 0, RC-67) and the answer names the hidden figures in `visibilite.masques`. A screen never
 * renders an absent figure as « 0,00 DA »: it says « Masqué ». Nothing here decides what is hidden.
 */
import { BUCKETS_RECAP, DROIT_VISIBILITE, LIBELLE_MONTANT_MASQUE } from "@pharmakon/shared/constants/visibilite-caissier.js";

export { DROIT_VISIBILITE, LIBELLE_MONTANT_MASQUE };

/** The word a figure's place shows when the policy hides it. */
export const MOT_MASQUE = "Masqué";

/** The hidden figures the server named for this reader (`visibilite.masques`), or none. */
export const masquesDe = (payload) => (Array.isArray(payload?.visibilite?.masques) ? payload.visibilite.masques : []);

/** Does the officine hide this figure (`DROIT_VISIBILITE`) from the reader of this payload? */
export const figureMasquee = (payload, droit) => masquesDe(payload).includes(droit);

/** Is `cle` absent from a loaded object — the server's policy removed it (null is a value, not absent)? */
export const estAbsent = (objet, cle) => objet != null && typeof objet === "object" && !Object.prototype.hasOwnProperty.call(objet, cle);

/** The cash of the cashier is hidden from him (the count is then blind for good — TD-348). */
export const especesMasquees = (payload) => figureMasquee(payload, DROIT_VISIBILITE.ESPECES);

const LIBELLES_BUCKETS = Object.freeze({
  ca_medicament: { label: "Médicament", icon: "pill" },
  ca_parapharmacie: { label: "Parapharmacie", icon: "heart-pulse" },
  ca_cosmetique: { label: "Cosmétique", icon: "sparkles" },
  ca_orthopedie: { label: "Orthopédie", icon: "bone" },
  ca_autres: { label: "Autres (consommables, matériel, compléments)", icon: "package" },
});

/**
 * The rows of « Ma session » (`GET /caisse/session-insights`), in reading order: the cash, the count, the
 * total, the families, the psychotropes. A figure absent from the payload reads « Masqué » (`masque`),
 * a cash withheld by the blind count only (TD-20, null) reads « Révélé à la clôture ».
 * @param {object} data the recap payload
 * @param {{ money: (v: number) => string, entier: (v: number) => string }} fmt
 * @returns {Array<{ key: string, label: string, icon: string, value: string, masque: boolean, strong?: boolean }>}
 */
export function lignesRecap(data, fmt) {
  const ligne = (key, label, icon, format, extra = {}) => {
    const masque = estAbsent(data, key);
    return { key, label, icon, masque, value: masque ? MOT_MASQUE : format(data[key]), ...extra };
  };
  return [
    ligne("caisse_amount", "En caisse", "wallet", (v) => (v == null ? "Révélé à la clôture" : fmt.money(v)), { strong: true }),
    ligne("nb_ventes", "Ventes", "receipt", fmt.entier),
    ligne("ca_total", "Total des ventes", "banknote", fmt.money),
    ...BUCKETS_RECAP.map((k) => ligne(k, LIBELLES_BUCKETS[k].label, LIBELLES_BUCKETS[k].icon, fmt.money)),
    ligne("ca_psychotrope", "Psychotrope", "shield-alert", fmt.money),
  ];
}
